import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import Can from '../../../../features/components/Can'
import ListItemsTable from '../../../../features/components/ListItemsTable'
import SocialAuthChecker from '../../../../features/components/SocialAuthChecker'
import CampaignDv360Edit from '../../../../forms/DV360/CampaignForms/CampaignDv360Edit'
import CampaignsChangeHistory from '../../CampaignsChangeHistory'

import useCampaignFetchData from '../../hooks'
import useGetDv360CampaignActions from './useGetDv360CampaignActions'

import { createCampaignSummaryPageLink } from '../../helpers'

import {
  campaignsSelector,
  campaignsIsLoadingSelector,
  campaignsWasLoadedSelector,
  campaignsErrorSelector,
  campaignUpdateSelector,
  campaignUpdateIsLoadingSelector,
  campaignDeleteSelector,
  campaignsAfterSelector,
  campaignsNextSelector,
  campaignSessionIdSelector
} from '../../../../modules/selectors/campaigns'
import { selectedDV360AdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { DV_360_PLATFORM } from '../../../../constants/selectLists/platformList'
import { ROUTE_PARAMS } from '../../../../constants/routes'

const DV360CampaignsSection = () => {
  const campaigns = useSelector(campaignsSelector)
  const campaignsAfter = useSelector(campaignsAfterSelector)
  const campaignsNext = useSelector(campaignsNextSelector)
  const campaignsSessionId = useSelector(campaignSessionIdSelector)

  const campaignDelete = useSelector(campaignDeleteSelector)
  const campaignUpdate = useSelector(campaignUpdateSelector)
  const campaignUpdateIsLoading = useSelector(campaignUpdateIsLoadingSelector)
  const { id: dv360AdvertiserId, currency_symbol: currencySymbol } = useSelector(selectedDV360AdAccountSelector)

  const { id: campaignUpdateId } = campaignUpdate
  const { id: campaignDeleteId, isLoading: campaignDeleteIsLoading } = campaignDelete

  const campaignActions = useGetDv360CampaignActions()

  const getCampaignsData = useCampaignFetchData({
    adAccountId: dv360AdvertiserId,
    platform: DV_360_PLATFORM,
    withPagination: true
  })

  const summaryPageLinkExtraParams = useMemo(
    () => ({
      [ROUTE_PARAMS.adAccount]: dv360AdvertiserId
    }),
    [dv360AdvertiserId]
  )

  const loadMoreCampaignsHandler = useCallback(() => {
    getCampaignsData(campaignsAfter, campaignsSessionId, { skipClearingCampaigns: true })
  }, [getCampaignsData, campaignsAfter, campaignsSessionId])

  return (
    <SocialAuthChecker
      platform={DV_360_PLATFORM}
      dataFetch={getCampaignsData}
      adAccountId={dv360AdvertiserId}
      authFailureText="Please authenticate with DV360 to retrieve the list of campaigns."
    >
      <ListItemsTable
        items={campaigns}
        platform={DV_360_PLATFORM}
        currencySymbol={currencySymbol}
        isLoadingSelector={campaignsIsLoadingSelector}
        errorSelector={campaignsErrorSelector}
        wasLoadedSelector={campaignsWasLoadedSelector}
        getActionsDropdownOptions={campaignActions}
        createItemLinkHelper={createCampaignSummaryPageLink}
        createItemLinkExtraParams={summaryPageLinkExtraParams}
        itemUpdatingId={campaignUpdateIsLoading ? campaignUpdateId : ''}
        itemDeletingId={campaignDeleteIsLoading ? campaignDeleteId : ''}
        loadMoreDataHandler={campaignsNext ? loadMoreCampaignsHandler : null}
      />
      <Can I="update" a="Campaign">
        <CampaignDv360Edit shouldBeCleared />
      </Can>
      <CampaignsChangeHistory platform={DV_360_PLATFORM} />
    </SocialAuthChecker>
  )
}

export default DV360CampaignsSection
